import {Card, FormControl, FormField} from 'platform/components';
import {Box, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useWatch} from 'react-hook-form';

import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {SIMULTANEOUS_RING_CAP} from '../../TelephonyGroups/utils/getTelephonyGroupSummary';
import {
  RING_SECONDS_MAX,
  RING_SECONDS_MIN,
  TelephonyGroupFormValues,
} from '../utils/telephonyGroupFormSchema';

interface TelephonyGroupRingStrategyCardProps extends RequiredTestIdProps {
  control: FormControl<TelephonyGroupFormValues>;
  isReadOnly: boolean;
}

const RING_SECONDS_FIELD_WIDTH = 60;

export function TelephonyGroupRingStrategyCard(props: TelephonyGroupRingStrategyCardProps) {
  const ringStrategy = useWatch({control: props.control, name: 'ringStrategy'});

  const strategyDescription = match(ringStrategy)
    .with('simultaneous', () =>
      i18n.t('page.settings.telephony.ringStrategySimultaneousDescription', {
        count: SIMULTANEOUS_RING_CAP,
      })
    )
    .with('sequential', () => i18n.t('page.settings.telephony.ringStrategySequentialDescription'))
    .exhaustive();

  const ringSecondsLabel = match(ringStrategy)
    .with('simultaneous', () => i18n.t('page.settings.telephony.ringSecondsGroup'))
    .with('sequential', () => i18n.t('page.settings.telephony.ringSecondsPerMember'))
    .exhaustive();

  return (
    <Card
      title={i18n.t('page.settings.telephony.ringStrategyCardTitle')}
      data-testid={suffixTestId('card', props)}
    >
      <VStack spacing={4}>
        <FormField
          control={props.control}
          name="ringStrategy"
          type="radio"
          options={[
            {
              label: i18n.t('page.settings.telephony.ringStrategySimultaneous'),
              value: 'simultaneous',
            },
            {
              label: i18n.t('page.settings.telephony.ringStrategySequential'),
              value: 'sequential',
            },
          ]}
          isDisabled={props.isReadOnly}
          data-testid={suffixTestId('ringStrategy', props)}
        />
        <Text
          size="small"
          color="secondary"
          data-testid={suffixTestId('strategyDescription', props)}
        >
          {strategyDescription}
        </Text>
        <Box width={RING_SECONDS_FIELD_WIDTH}>
          <FormField
            control={props.control}
            name="ringSeconds"
            type="number"
            label={ringSecondsLabel}
            suffix={i18n.t('page.settings.telephony.secondsSuffix')}
            helperText={i18n.t('page.settings.telephony.ringSecondsHelper', {
              min: RING_SECONDS_MIN,
              max: RING_SECONDS_MAX,
            })}
            isRequired
            isDisabled={props.isReadOnly}
            data-testid={suffixTestId('ringSeconds', props)}
          />
        </Box>
      </VStack>
    </Card>
  );
}
